/**
 * API Health Store — Zustand
 * Tracks consecutive failures to surface "API Down" banner.
 * Works outside React components via .getState() in Axios interceptors.
 */
import { create } from 'zustand';

/** After this many consecutive non-4xx failures, apiDown = true */
const FAILURE_THRESHOLD = 3;

interface ApiHealthState {
  apiDown: boolean;
  consecutiveFailures: number;
  lastError: string | null;

  /** Call from Axios response error interceptor (network / 5xx only) */
  reportFailure: (error?: string) => void;
  /** Call from Axios response success interceptor */
  reportSuccess: () => void;
}

export const useApiHealthStore = create<ApiHealthState>((set, get) => ({
  apiDown: false,
  consecutiveFailures: 0,
  lastError: null,

  reportFailure: (error?: string) => {
    const failures = get().consecutiveFailures + 1;
    set({
      consecutiveFailures: failures,
      lastError: error ?? null,
      apiDown: failures >= FAILURE_THRESHOLD,
    });
  },

  reportSuccess: () => {
    // Only reset if we were actually down — avoids noise on normal traffic
    if (get().consecutiveFailures > 0) {
      set({ apiDown: false, consecutiveFailures: 0, lastError: null });
    }
  },
}));
